import React, { useState, useCallback, useEffect, useRef } from 'react';
import { SentryClient } from '@smart-tv/sentry/SentryClient';
import { closeStream } from '@smart-tv/utils/kmm/closeStream';
import { tokenStorage } from '@smart-tv/utils/kmm/kmm';
import { loginWithEmailPassword } from '@smart-tv/utils/kmm/ntvb-login';
import { TokensNetworkModel } from '@norlys-a-s/norlys-play-kmm';
import { useNetwork } from '@smart-tv/hooks/use-network/use-network';

import {
  ensureLocalStorageMatchIDB,
  removePersistentData,
  retrievePersistentData
} from '@smart-tv/utils/persistent-data/persistent-data';

export interface AuthenticationContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface AuthenticationContextValues {
  authenticated: boolean;
  isAuthenticationResolved: boolean;
  sendCredentials: (email: string, password: string) => Promise<TokensNetworkModel>;
  removeAuthentication: () => void;
  refreshAuthState: () => void;
  closeActiveStreamIfExists: () => Promise<void>;
}

export const AuthenticationContext = React.createContext<AuthenticationContextValues>(
  {} as AuthenticationContextValues
);

export const AuthenticationConsumer = AuthenticationContext.Consumer;

export const AuthenticationProvider = (props: AuthenticationContextProps): JSX.Element => {
  const { children } = props;

  const [authenticated, setAuthenticated] = useState(false);

  const [isAuthenticationResolved, setIsAuthenticationResolved] = useState(false);

  const authenticationSubscriptionRef = useRef<any>(null);

  const { checkNetwork, setIsOffline } = useNetwork();

  // Close active stream if exists and less than 6 minutes have passed
  const closeActiveStreamIfExists = useCallback(async () => {
    const activeStreamData = retrievePersistentData<{ params: any; timestamp: number }>(
      'active-stream-set'
    );
    if (activeStreamData) {
      const elapsedTime = Date.now() - activeStreamData.timestamp;
      const sixMinutesInMs = 6 * 60 * 1000;
      if (elapsedTime < sixMinutesInMs) {
        try {
          await closeStream(activeStreamData.params);
        } catch (error) {
          console.error('Failed to close active stream:', error);
        }
      } else {
        removePersistentData('active-stream-set');
      }
    }
  }, []);

  const sendCredentials = useCallback(async (email: string, password: string) => {
    return await loginWithEmailPassword({ email, password });
  }, []);

  const removeAuthentication = useCallback(() => {
    tokenStorage.clearTokens(true);
    removePersistentData('access_token');
    removePersistentData('refresh_token');
    removePersistentData('favorites-list');
    removePersistentData('active-stream-set');
    SentryClient.removeUser();
    setAuthenticated(false);
  }, []);

  const refreshAuthState = useCallback(() => {
    setAuthenticated(tokenStorage.isAuthenticated);
  }, []);

  // Auto-login with injected test credentials (dev builds only)
  const handleAutoLogin = useCallback(async () => {
    const testAuth = (window as any).__TEST_AUTH__;
    if (testAuth?.email && testAuth?.password) {
      try {
        await sendCredentials(testAuth.email, testAuth.password);
        setAuthenticated(true);
        console.log('Auto-login successful');
      } catch (error) {
        console.error('Auto-login failed:', error);
      }
    }
    setIsAuthenticationResolved(true);
  }, [sendCredentials]);

  /**
   * Subscribe to KMM authentication state flow
   * Inside the KMM package there is Flow<AuthenticationState> that emits new AuthenticationState on every change
   * And as Kotlin Flow cant be used i Javascript it is wrapped inside JsFlowWrapper which is actually just an observable
   * we observe its state by its subscribe method and we stop observing and cancel in useEffect cleanup method
   **/

  useEffect(() => {
    if ((window as any).__TEST_AUTH__) {
      handleAutoLogin();
      return;
    }

    const initSubscribtionFlow = async () => {
      await ensureLocalStorageMatchIDB();
      const authenticationSubscription = tokenStorage.jsIsAuthenticatedFlow.subscribe(
        async (authState) => {
          authenticationSubscriptionRef.current = authenticationSubscription;

          const isOnline = await checkNetwork();

          if (isOnline) {
            const isAuthenticated = authState.isAuthenticated;
            setAuthenticated(isAuthenticated);
            if (!isAuthenticated) {
              SentryClient.removeUser();
            }
            setIsAuthenticationResolved(true);
          } else {
            setIsOffline(true);
            setIsAuthenticationResolved(true);
          }
        },
        () => {
          console.error('Error subscribing to authentication state');
          setIsAuthenticationResolved(true);
        }
      );
    };
    initSubscribtionFlow();

    return () => {
      authenticationSubscriptionRef.current?.cancel();
    };
  }, [handleAutoLogin, checkNetwork, setIsOffline]);

  return (
    <AuthenticationContext.Provider
      value={{
        authenticated,
        sendCredentials,
        isAuthenticationResolved,
        removeAuthentication,
        refreshAuthState,
        closeActiveStreamIfExists
      }}
    >
      {isAuthenticationResolved ? children : null}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationContext;
